import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useDomain } from "@useDomain";
import api from "@api";
import { Helmet } from "react-helmet-async";
import templateRegistry from "../pages/Dashboard/PageBuilder/Registry/index.js";
import systemPageRegistry from "../pages/Dashboard/PageBuilder/Registry/systemPageRegistry.js";
import NotFound from "../pages/System/NotFound.jsx";

function DynamicOrgPage() {
   const { pagePath } = useParams();
   const { slug, domain, organization } = useDomain();
   const [pageData, setPageData] = useState(null);
   const [loadingPage, setLoadingPage] = useState(true);
   const { setPageReady } = useOutletContext();
   useEffect(() => {
      let alive = true;
      setPageReady?.(false);
      setLoadingPage(true);
      const loadPublicPage = async () => {
         try {
            setLoadingPage(true);
            const res = await api.get("/api/onboarding/organizations/public/page", {
               params: {
                  slug: slug || undefined,
                  domain: domain || undefined,
                  organizationId: organization?.id || undefined,
                  path: pagePath || undefined,
               },
            });
            if (!alive) return;
            setPageData(res.data || null);
  
         } catch (error) {
            console.error("Failed to load public page:", error);
            if (!alive) return;
            setPageData(null);
         } finally {
            if (alive) {
               setLoadingPage(false);
               setPageReady?.(true);
            }
         }
      };

      if (slug || domain || organization?.id) {
         loadPublicPage();
      } else {
         setPageData(null);
         setLoadingPage(false);
         setPageReady?.(true);
      }

      return () => {
         alive = false;
      };
   }, [slug, domain, organization?.id, pagePath]);

   if (loadingPage) return null;
   if (!organization) return <NotFound />;
   if (organization.website_visible === false) return <NotFound />;
   if (!pageData) return <NotFound />;

   const orgName = pageData?.organization?.name || organization?.name || "SportingHip";
   const page = pageData?.page || {};
   const content = page?.content || {};
   const systemConfig =
      page?.page_type === "system"
         ? systemPageRegistry[page.system_key]
         : null;

   let seoTitle = orgName;
   let seoDescription = `Learn more about ${orgName}.`;

   if (page?.page_type === "system") {
      const systemLabel =
         systemConfig?.label ||
         page.name ||
         "Page";
      seoTitle = `${systemLabel} | ${orgName}`;
      seoDescription = `View ${systemLabel.toLowerCase()} for ${orgName}.`;
   } else {
      const customPageName = page.name || content.title || "Page";
      seoTitle = `${customPageName} | ${orgName}`;
      seoDescription =
         content.about ||
         content.intro_text ||
         content.headline ||
         content.hero_subtitle ||
         `Learn more about ${customPageName} for ${orgName}.`;
   }
   if (page?.page_type === "system") {
      const SystemComponent = systemConfig?.publicComponent;
      if (!SystemComponent) {
         return <NotFound />;
      }


      return (
         <>
            <Helmet>
               <title>{seoTitle}</title>
               <meta name="description" content={seoDescription} />
               <meta property="og:title" content={seoTitle} />
               <meta property="og:description" content={seoDescription} />
               <meta property="og:site_name" content={orgName} />
               <link
                  rel="canonical"
                  href={window.location.origin + window.location.pathname}
               />
            </Helmet>

            <SystemComponent
               page={page}
               organization={pageData.organization}
               settings={pageData.settings}
               pageData={pageData}
            />
         </>
      );
   }

   const templateId = pageData?.page?.template_id;
   const TemplateComponent =
      templateRegistry[templateId]?.publicComponent;

   if (!TemplateComponent) {
      return <NotFound />;
   }
   return (
      <>
         <Helmet>
            <title>{seoTitle}</title>
            <meta name="description" content={seoDescription} />
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:site_name" content={orgName} />
            <link rel="canonical" href={window.location.origin + window.location.pathname} />
         </Helmet>

         <TemplateComponent
            organization={pageData.organization}
            page={pageData.page}
            settings={pageData.settings}
            pageData={pageData}
         />
      </>
   );
}

export default DynamicOrgPage;